import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { CompositionDisplayContext } from '../lib/composition-display.js';
import { startTransition, Suspense, useMemo, useRef, useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IconHome, IconCalendar, IconDashboard, IconCash, IconRefresh,
  IconWindow, IconInbox, IconGift, IconRoute, IconUsers,
} from '../ui/icons.js';
import { useAuth } from '../stores/auth.js';
import { useUI } from '../stores/ui.js';
import { usePullToRefresh, PTR_TRIGGER_PX } from '../lib/usePullToRefresh.js';
import { queryClient } from '../lib/queryClient.js';
import { ordersListQueryOptions } from '../features/orders/queries.js';
import { priceBookAccessoriesQuery, priceBookOfferQuery } from '../features/pricebook/queries.js';
import type { Role } from '@jaluzi/types';

/** Роли, которые заполняют замер в калькуляторе — им прайс нужен и без сети. */
const CALC_ROLES: Role[] = ['MEASURER', 'MANAGER', 'ADMIN'];

interface Tab {
  to: string;
  /** Ключ перевода в namespace `tab`. */
  labelKey: string;
  icon: typeof IconHome;
  badge?: number;
  /** Если задано — вкладка видна только для перечисленных ролей. */
  roles?: Role[];
  /** Раздел на паузе — вкладку не показываем (маршрут остаётся, ведёт на заглушку). */
  paused?: boolean;
  /** Если задано — вкладка скрыта для перечисленных ролей. */
  hideForRoles?: Role[];
}

/* Профиль и Уведомления живут как bottom-sheet (открываются из шапки).
 * Здесь — только основная навигация по разделам.
 * Касса видна только установщику; Неделя скрыта для цеха.
 * Клиенту — «Мои заказы» + «Каталог» (своя пара: смотреть прогресс + выбирать новое).
 *
 * Лейбл первой вкладки динамический: для CLIENT — «Мои заказы», для всех остальных — «Сегодня».
 * Сделано через специальный ключ ниже в Shell-рендере. */
// Приём запросов на расчёт от клиентов приостановлен (сервер: QuotesPausedGuard).
// Вернуть вкладки «Запросы» и «Каталог» — поставить false.
const CLIENT_QUOTES_PAUSED = true;

const ALL_TABS: Tab[] = [
  { to: '/',           labelKey: 'tab.today',     icon: IconHome, hideForRoles: ['PRODUCTION', 'DEALER', 'DRIVER', 'QC', 'LOGIST', 'ACCOUNTANT', 'INSTALLATION_MANAGER', 'WAREHOUSE_MANAGER'] },
  { to: '/quotes',     labelKey: 'tab.quotes',    icon: IconInbox, roles: ['MANAGER', 'ADMIN'], paused: CLIENT_QUOTES_PAUSED },
  { to: '/catalog',    labelKey: 'tab.catalog',   icon: IconWindow, roles: ['CLIENT'], paused: CLIENT_QUOTES_PAUSED },
  { to: '/referrals',  labelKey: 'tab.referrals', icon: IconGift, roles: ['CLIENT'] },
  { to: '/week',       labelKey: 'tab.week',      icon: IconCalendar, hideForRoles: ['PRODUCTION', 'CLIENT', 'ACCOUNTANT', 'DRIVER', 'QC', 'DEALER', 'LOGIST', 'INSTALLATION_MANAGER', 'WAREHOUSE_MANAGER'] },
  // «Замеры» (замерщик) / «Монтажи» (монтажник) — все назначенные заказы. Подпись динамическая (см. ниже).
  { to: '/assignments', labelKey: 'tab.measures', icon: IconWindow, roles: ['MEASURER', 'INSTALLER'] },
  // «Роли» — администратор открывает окна замерщика, монтажника, водителя и ОТК
  // (переключатель и выбор сотрудника — сверху в разделе).
  { to: '/roles', labelKey: 'tab.roles', icon: IconUsers, roles: ['ADMIN'] },
  { to: '/dashboard',  labelKey: 'tab.dashboard', icon: IconDashboard, hideForRoles: ['CLIENT', 'DRIVER', 'QC', 'DEALER', 'INSTALLATION_MANAGER', 'WAREHOUSE_MANAGER'] },
  // Касса — монтажник (оплата после монтажа) И замерщик (аванс при замере): оба
  // принимают деньги и сдают их кассовой сменой (daily-closes уже разрешён обоим).
  { to: '/cashbox',    labelKey: 'tab.cashbox',   icon: IconCash, roles: ['INSTALLER', 'MEASURER'] },
  // «Касса» бухгалтера — контроль: утверждение смен + подтверждение поступлений/заявок.
  { to: '/cashdesk',   labelKey: 'tab.cashdesk',  icon: IconCash, roles: ['ACCOUNTANT'] },
  // Окно водителя — 3 вкладки: забор из цеха → сдача в ОТК → сводка.
  { to: '/driver/pickup',  labelKey: 'tab.driverPickup',  icon: IconInbox,     roles: ['DRIVER'] },
  { to: '/driver/deliver', labelKey: 'tab.driverDeliver', icon: IconRoute,     roles: ['DRIVER'] },
  { to: '/driver/summary', labelKey: 'tab.driverSummary', icon: IconDashboard, roles: ['DRIVER'] },
  // Окно ОТК — 3 вкладки: проверка → брак → сводка.
  { to: '/qc/check',   labelKey: 'tab.qcCheck',   icon: IconInbox,     roles: ['QC'] },
  { to: '/qc/defects', labelKey: 'tab.qcDefects', icon: IconWindow,    roles: ['QC'] },
  { to: '/qc/summary', labelKey: 'tab.qcSummary', icon: IconDashboard, roles: ['QC'] },
  // Дилерский кабинет — единственный таб для роли DEALER.
  { to: '/d',          labelKey: 'tab.dealer',    icon: IconHome, roles: ['DEALER'] },
  // Мобильный диспетчер — единственный рабочий таб для роли LOGIST.
  { to: '/dispatch',   labelKey: 'tab.dispatch',  icon: IconRoute, roles: ['LOGIST', 'INSTALLATION_MANAGER'] },
  { to: '/installation/defects', labelKey: 'tab.installationDefects', icon: IconWindow, roles: ['INSTALLATION_MANAGER'] },
  { to: '/installation/quality', labelKey: 'tab.installationQuality', icon: IconDashboard, roles: ['INSTALLATION_MANAGER'] },
  { to: '/warehouse/finished', labelKey: 'tab.warehouseFinished', icon: IconInbox, roles: ['WAREHOUSE_MANAGER'] },
  // Производство работает из двух разделённых очередей: внутренний цех и внешние подрядчики.
  { to: '/production/own', labelKey: 'tab.productionOwn', icon: IconWindow, roles: ['PRODUCTION'] },
  { to: '/production/contractors', labelKey: 'tab.productionContractors', icon: IconInbox, roles: ['PRODUCTION'] },
];

export function Shell() {
  const displayFlags = useQuery<Record<string, unknown>>({
    queryKey: ['public-flags'],
    queryFn: () => api('/v1/settings/public-flags', { cache: 'no-store' }),
    staleTime: 30_000, refetchInterval: 30_000,
  });
  const displayMode = displayFlags.data?.['orders.composition_display_mode'] === 'per_item' ? 'per_item' : 'compact';
  const { t } = useTranslation();
  const role = useAuth((s) => s.user?.role);
  const extraRoles = useAuth((s) => s.user?.roles);
  const onRefresh = useUI((s) => s.onRefresh);
  const mainRef = useRef<HTMLElement>(null);
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // mainRef → state, чтобы хук перерегистрировался при ре-маунте
  useEffect(() => {
    setScrollEl(mainRef.current);
  }, []);

  // Нижние вкладки — самостоятельные рабочие разделы. При переходе между ними
  // не переносим позицию длинного списка из предыдущей очереди.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  // При возврате в приложение (переключение вкладки/разворачивание) перечитываем
  // профиль с сервера — чтобы UI-настройки, изменённые на ДРУГОМ устройстве
  // (стиль формы замера, вид карточки заказа), подхватились без перезагрузки.
  useEffect(() => {
    const onFocusBack = () => {
      if (document.visibilityState === 'visible') void useAuth.getState().refreshMe();
    };
    document.addEventListener('visibilitychange', onFocusBack);
    window.addEventListener('focus', onFocusBack);
    return () => {
      document.removeEventListener('visibilitychange', onFocusBack);
      window.removeEventListener('focus', onFocusBack);
    };
  }, []);

  const ptr = usePullToRefresh(scrollEl, onRefresh);

  // Вкладки гейтим по ЭФФЕКТИВНЫМ ролям (мультироль: primary + extraRoles),
  // а не по одной primary. roles-вкладка видна, если ЛЮБАЯ эфф. роль подходит;
  // hideForRoles прячет, только если ВСЕ эфф. роли скрыты (иначе замерщик+бухгалтер
  // терял бы либо «Кассу», либо замерные вкладки). См. project-multirole.
  const effRoles = useMemo(
    () => (extraRoles && extraRoles.length > 0 ? extraRoles : role ? [role] : []),
    [extraRoles, role],
  );
  const tabs = useMemo(
    () => ALL_TABS.filter((t) => {
      if (t.paused) return false;
      if (t.roles && !t.roles.some((r) => effRoles.includes(r))) return false;
      if (t.hideForRoles && effRoles.length > 0 && effRoles.every((r) => t.hideForRoles!.includes(r))) return false;
      return true;
    }),
    [effRoles],
  );

  // На iPhone 14 первый рендер lazy-раздела успевал показать один кадр
  // skeleton, тогда как 16 Pro Max уже получал данные из кеша. После mount
  // спокойно прогреваем основные chunks и общий список заказов:
  // при тапе старый раздел остаётся до полностью готового нового экрана.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      void Promise.allSettled([
        import('../pages/WeekPage.js'),
        import('../pages/AssignmentsPage.js'),
        import('../pages/DashboardPage.js'),
        import('../pages/CashboxPage.js'),
        queryClient.prefetchQuery(ordersListQueryOptions()),
        // Прайс и флаги — сразу при запуске, пока есть сеть: service worker кладёт их
        // в офлайн-кеш, и форма замера заполняется даже если калькулятор сегодня
        // ещё не открывали, а связь пропала на объекте.
        ...(effRoles.some((r) => CALC_ROLES.includes(r)) ? [
          queryClient.prefetchQuery(priceBookOfferQuery),
          queryClient.prefetchQuery(priceBookAccessoriesQuery),
          queryClient.prefetchQuery({ queryKey: ['public-flags'], queryFn: () => api('/v1/settings/public-flags', { cache: 'no-store' }) }),
        ] : []),
      ]);
    }, 250);
    return () => window.clearTimeout(timer);
    // Ключ — строка ролей: /auth/me при каждом возврате в приложение приносит
    // новый массив ролей, и прогрев запускался заново на тех же ролях.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effRoles.join(',')]);

  const showIndicator = ptr.pull > 0 || ptr.refreshing;

  return (
    <div className="jb-shell">
      {/* Pull-to-refresh индикатор */}
      {showIndicator && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            paddingBottom: 8,
            height: Math.max(ptr.pull, ptr.refreshing ? PTR_TRIGGER_PX * 0.7 : 0),
            zIndex: 90,
            pointerEvents: 'none',
            transition: ptr.refreshing ? 'height 0.18s ease' : 'none',
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'var(--surface-alt)',
              border: '1px solid var(--hairline-strong)',
              boxShadow: '0 4px 12px -4px rgba(0,0,0,0.4)',
              display: 'grid',
              placeItems: 'center',
              color: ptr.ready || ptr.refreshing ? 'var(--accent)' : 'var(--text-muted)',
              transform: `rotate(${ptr.refreshing ? 0 : ptr.pull * 4}deg)`,
              animation: ptr.refreshing ? 'jb-spin 0.9s linear infinite' : undefined,
              transition: ptr.refreshing ? 'none' : 'color 0.15s',
            }}
          >
            <IconRefresh width={18} height={18} />
          </div>
        </div>
      )}

      <main className="jb-shell__main" ref={mainRef}>
        <Suspense fallback={<div role="status" style={{ padding: 20, color: 'var(--text-muted)' }}>Загрузка…</div>}>
          <CompositionDisplayContext.Provider value={displayMode}><Outlet /></CompositionDisplayContext.Provider>
        </Suspense>
      </main>
      <nav
        className="jb-tab-bar"
        role="navigation"
        aria-label={t('a11y.main_nav')}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = tab.to === '/'
            ? location.pathname === '/'
            : location.pathname === tab.to || location.pathname.startsWith(`${tab.to}/`);
          // Для CLIENT главная вкладка «/» — это «Мои заказы», а не «Сегодня».
          // У других ролей оставляем «Сегодня».
          const label = tab.to === '/' && role === 'CLIENT'
            ? t('tab.my_orders')
            : tab.to === '/assignments'
            ? t(effRoles.includes('MEASURER') && effRoles.includes('INSTALLER') ? 'tab.visits'
              : effRoles.includes('INSTALLER') ? 'tab.installs' : 'tab.measures')
            : tab.to === '/dispatch' && role === 'INSTALLATION_MANAGER'
            ? t('tab.installs')
            : t(tab.labelKey);
          return (
            <button
              type="button"
              key={tab.to}
              className={'jb-tab' + (isActive ? ' is-active' : '')}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => {
                if (!isActive) {
                  startTransition(() => {
                    void navigate(tab.to);
                  });
                }
              }}
            >
              <span className="jb-tab__icon"><Icon /></span>
              <span>{label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
